import { useCallback, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import type {
  DiscountVoucherTargetItem,
  DiscountVoucherTargetSeason,
  DiscountVoucherTerm,
} from '@/types/approval';
import { formatVoucherNumber } from '@/utils/approval/discountVoucher';
import { DiscountVoucherTables, DiscountVoucherToggleRow } from './styles';

const TABLE_CLASS =
  'table table-bordered table-striped table-responsive no-margin';
const BLOCK_STYLE = { margin: '5px 0' };

type IssueTableProps = {
  brandName: string;
  shopCode: string | null;
  shopName: string | null;
  terms: DiscountVoucherTerm[];
  targetSeasons: DiscountVoucherTargetSeason[];
  t: TFunction;
};

/** 레거시 appr_sub_view_forms_coupon 마크업 그대로 — 레거시 문서와 같은 HTML 로 저장 */
const DiscountVoucherIssueTables = ({
  brandName,
  shopCode,
  shopName,
  terms,
  targetSeasons,
  t,
}: IssueTableProps) => {
  const won = (value: string | null) =>
    t('{{amount}}원', { amount: formatVoucherNumber(value) });
  const first = terms[0];
  const content = `[${first?.voucherTypeName ?? ''}]${
    first?.description ?? ''
  }`;
  return (
    <>
      <div style={BLOCK_STYLE}>
        <table className={TABLE_CLASS}>
          <tbody>
            <tr>
              <th className="text-center bg-info" colSpan={8}>
                {t('할인권 발행')}
              </th>
            </tr>
            {shopCode ? (
              <>
                <tr>
                  <th className="text-center" colSpan={2}>
                    {t('브랜드')}
                  </th>
                  <th className="text-center" colSpan={2}>
                    {t('매장')}
                  </th>
                  <th className="text-center" colSpan={4}>
                    {t('내용', { context: 'detail' })}
                  </th>
                </tr>
                <tr>
                  <td className="text-center" colSpan={2}>
                    {brandName}
                  </td>
                  <td className="text-center" colSpan={2}>
                    {shopName}
                  </td>
                  <td className="text-center" colSpan={4}>
                    {content}
                  </td>
                </tr>
              </>
            ) : (
              <>
                <tr>
                  <th className="text-center" colSpan={2}>
                    {t('브랜드')}
                  </th>
                  <th className="text-center" colSpan={6}>
                    {t('내용', { context: 'detail' })}
                  </th>
                </tr>
                <tr>
                  <td className="text-center" colSpan={2}>
                    {brandName}
                  </td>
                  <td className="text-center" colSpan={6}>
                    {content}
                  </td>
                </tr>
              </>
            )}
            <tr>
              <th className="text-center">{t('최소구매')}</th>
              <th className="text-center">{t('할인상한')}</th>
              <th className="text-center">{t('할인단위')}</th>
              <th className="text-center">{t('수량')}</th>
              <th className="text-center">{t('할인단가')}</th>
              <th className="text-center">{t('할인품목')}</th>
              <th className="text-center">{t('유효기간')}</th>
              <th className="text-center">{t('온/오프통합')}</th>
            </tr>
            {terms.map((term, i) => (
              <tr key={i}>
                <td className="text-center">
                  {won(term.minimumPurchaseAmount)}
                </td>
                <td className="text-center">
                  {won(term.maximumDiscountAmount)}
                </td>
                <td className="text-center">
                  {term.discountUnitType === 'A'
                    ? `${term.discountUnit ?? ''}%`
                    : won(term.discountUnit)}
                </td>
                <td className="text-center">
                  {formatVoucherNumber(term.quantity)}
                </td>
                <td className="text-center">
                  {term.priceBasis === 'A'
                    ? t('택가')
                    : t('실판가', { context: 'price' })}
                </td>
                <td className="text-center">
                  {term.itemScope === 'ALL' ? t('전체') : t('특정상품')}
                </td>
                <td className="text-center">
                  {`${term.effectiveFrom ?? ''}~${term.effectiveTo ?? ''}`}
                </td>
                <th className="text-center">
                  {term.channelTarget === 'A' ? 'Y' : 'N'}
                </th>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {targetSeasons.length > 0 && (
        <div style={BLOCK_STYLE}>
          <table className={TABLE_CLASS}>
            <colgroup>
              <col width="50%" />
              <col width="50%" />
            </colgroup>
            <tbody>
              <tr>
                <th className="text-left" colSpan={2}>
                  {t('할인대상 품목')}
                </th>
              </tr>
              <tr>
                <th className="text-center">{t('시즌')}</th>
                <th className="text-center">{t('제품')}</th>
              </tr>
              {targetSeasons.map((row, i) => (
                <tr key={i}>
                  <td className="text-center">{row.season}</td>
                  <td className="text-center">
                    {t('{{amount}} 건', {
                      amount: formatVoucherNumber(row.itemCount),
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

/** 레거시 appr_sub_view_forms_coupon_parts 마크업 그대로 */
const DiscountVoucherItemTable = ({
  items,
  t,
}: {
  items: DiscountVoucherTargetItem[];
  t: TFunction;
}) => (
  <div style={BLOCK_STYLE}>
    <table className={TABLE_CLASS}>
      <tbody>
        <tr>
          <th className="text-left" colSpan={4}>
            {t('할인품목 상세내역')}
          </th>
        </tr>
        <tr>
          <th className="text-center">{t('시즌')}</th>
          <th className="text-center">{t('품번')}</th>
          <th className="text-center">{t('품명')}</th>
          <th className="text-center">{t('택가')}</th>
        </tr>
        {items.map((item, i) => (
          <tr key={i}>
            <td className="text-center">{item.season}</td>
            <td className="text-center">{item.partCode}</td>
            <td className="text-center">{item.name}</td>
            <td className="text-center">
              {formatVoucherNumber(item.tagPrice)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** attributes.voucherIssueHtml — 할인권 발행 표 + 할인대상 품목 표 */
export const buildDiscountVoucherIssueHtml = (
  props: Omit<IssueTableProps, 't'>,
  t: TFunction,
): string =>
  renderToStaticMarkup(<DiscountVoucherIssueTables {...props} t={t} />);

/** attributes.discountItemDetailsHtml — 대상 품목이 없으면 null(레거시도 빈 값) */
export const buildDiscountItemDetailsHtml = (
  items: DiscountVoucherTargetItem[],
  t: TFunction,
): string | null =>
  items.length
    ? renderToStaticMarkup(<DiscountVoucherItemTable items={items} t={t} />)
    : null;

const DROP_TAGS = new Set(['style', 'script', 'link']);

type SnapshotProps = {
  voucherIssueHtml: string | null;
  discountItemDetailsHtml: string | null;
};

/** 저장 HTML 그대로 표시(작성·조회 공용). 할인품목 상세는 보기/닫기로 펼침 — 레거시 동일 */
const DiscountVoucherSnapshot = ({
  voucherIssueHtml,
  discountItemDetailsHtml,
}: SnapshotProps) => {
  const { t } = useTranslation();
  const [isItemsOpen, setIsItemsOpen] = useState(false);
  const transform = useCallback<ElementTransform>(
    ({ tagName }) => (DROP_TAGS.has(tagName) ? null : undefined),
    [],
  );
  const issue = useHtmlToReact(voucherIssueHtml ?? '', { transform });
  const items = useHtmlToReact(discountItemDetailsHtml ?? '', { transform });

  return (
    <>
      <DiscountVoucherTables>{issue}</DiscountVoucherTables>
      {discountItemDetailsHtml && (
        <>
          <DiscountVoucherToggleRow>
            <BoxButton
              type="button"
              variant="secondary"
              size="small"
              onClick={() => setIsItemsOpen((open) => !open)}
            >
              {isItemsOpen
                ? t('할인품목 상세내역 닫기')
                : t('할인품목 상세내역 보기')}
            </BoxButton>
          </DiscountVoucherToggleRow>
          {isItemsOpen && (
            <DiscountVoucherTables>{items}</DiscountVoucherTables>
          )}
        </>
      )}
    </>
  );
};

export default DiscountVoucherSnapshot;
